import { FormattedMessage } from 'react-intl'

import Stack, { StackProps } from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import AuctionBidLabel from 'app/components/common/AuctionBidLabel'
import useAppStore from 'app/store/app.store'
import { defaultCurrency } from 'app/utils/constants/intl.constants'

import { usePrice } from '../hooks/usePrice'

import PersonalPrice from './PersonalPrice'
import TradeBusinessPrice from './TradeBusinessPrice'

interface PriceProps {
  actualPrice?: number
  convertedActualPrice?: number
  isAuction?: boolean
  userCurrency?: string
  listingCurrency?: Currency
  isCurrentBid?: boolean
  isHighestBidder?: boolean
  isOutbid?: boolean
  sx?: StackProps['sx']
  convertedTradePrice?: number
  tradePrice?: number
}

const Price: RFC<PriceProps> = ({
  actualPrice = 0,
  convertedActualPrice = 0,
  userCurrency = defaultCurrency,
  listingCurrency,
  isAuction,
  isCurrentBid = false,
  isHighestBidder = false,
  isOutbid = false,
  sx,
  convertedTradePrice = 0,
  tradePrice
}) => {
  const isTablet = useAppStore((state) => state.isTablet)

  const {
    actualPriceWithCurrency,
    approxConvertedPrice,
    showApprox,
    showBidLabel,
    showBusinessTradePrice,
    showPersonalPrice,
    isTradePrice
  } = usePrice({
    actualPrice,
    convertedActualPrice,
    userCurrency,
    listingCurrency,
    isAuction,
    isHighestBidder,
    isOutbid,
    convertedTradePrice,
    tradePrice
  })

  return (
    <Stack
      sx={{
        flexDirection: { md: isTablet ? 'row' : 'column', xs: 'row' },
        alignItems: { md: isTablet ? 'center' : 'flex-start', xs: 'center' },
        justifyContent: {
          md: isTablet ? 'center' : 'flex-start',
          xs: 'center'
        },
        marginTop: { xs: 0, md: isTablet ? 0 : 2 },
        ...sx
      }}
    >
      {isAuction && (
        <Text
          variant="subtitle1"
          component="p"
          color="primary.dark"
          sx={{ pr: 3 }}
        >
          {isCurrentBid ? (
            <FormattedMessage defaultMessage="Current Bid" />
          ) : (
            <FormattedMessage defaultMessage="Start Price" />
          )}
        </Text>
      )}

      <Stack direction="column">
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          {showPersonalPrice && (
            <PersonalPrice
              actualPriceWithCurrency={actualPriceWithCurrency}
              isTradePrice={isTradePrice}
            />
          )}

          {showBusinessTradePrice && (
            <TradeBusinessPrice
              actualPrice={actualPrice}
              isTradePrice={isTradePrice}
              listingCurrency={listingCurrency}
              tradePrice={tradePrice}
            />
          )}

          {showBidLabel && (
            <Stack sx={{ display: { xs: 'none', md: 'flex' } }}>
              <AuctionBidLabel isOutbid={isOutbid} />
            </Stack>
          )}
        </Stack>

        {showApprox && (
          <Text
            variant="subtitle1"
            component="p"
            color="text.third"
            sx={{
              textAlign: { xs: showBidLabel ? 'left' : 'center', md: 'left' }
            }}
          >
            <FormattedMessage
              defaultMessage="Approx. {price}"
              values={{ price: approxConvertedPrice }}
            />
          </Text>
        )}

        {showBidLabel && (
          <Stack sx={{ mt: 1, display: { xs: 'flex', md: 'none' } }}>
            <AuctionBidLabel isOutbid={isOutbid} />
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}

export default Price
